import { useMemo, useState, type ReactNode } from 'react'

import { cn } from '../utilitarios/cn'

export interface FatiaRosca {
  rotulo: string
  valor: number
  /** Percentual já calculado pelo servidor (uma casa). Ausente = derivado aqui. */
  percentual?: number
  /** Cor explícita (motivo de agendamento tem cor de cadastro). */
  cor?: string
  onSelecionar?: () => void
}

export interface PropriedadesRosca {
  fatias: ReadonlyArray<FatiaRosca>
  /** Número no centro. É o total do bloco, e é a informação principal. */
  centro: ReactNode
  legendaCentro?: string
  tamanho?: number
  espessura?: number
  /** Máximo de fatias antes de agrupar o resto em "Outros" (nunca inventar cor). */
  maximoFatias?: number
  className?: string
  onSelecionarCentro?: () => void
}

/** Ordem fixa da paleta categórica. A nona fatia vira "Outros", nunca cor nova. */
const CORES_SERIE = [
  'var(--serie-1)',
  'var(--serie-2)',
  'var(--serie-3)',
  'var(--serie-4)',
  'var(--serie-5)',
  'var(--serie-6)',
  'var(--serie-7)',
  'var(--serie-8)',
] as const

const COR_RESTO = 'var(--serie-resto)'

/**
 * O miolo do centro da rosca — número grande e legenda.
 *
 * Extraído porque o centro é `<button>` quando há drill-down e `<div>` quando não
 * há (ver o comentário do CI-3 abaixo), e duas cópias do mesmo par de `<span>`
 * divergem na primeira vez que alguém mexer numa delas.
 */
function ConteudoCentro({
  centro,
  legendaCentro,
}: {
  centro: ReactNode
  legendaCentro?: string
}) {
  return (
    <>
      <span className="text-2xl font-semibold leading-none text-conteudo">{centro}</span>
      {legendaCentro ? (
        <span className="mt-1 max-w-[70%] text-[0.6875rem] leading-tight text-conteudo-fraco">
          {legendaCentro}
        </span>
      ) : null}
    </>
  )
}

/**
 * Rosca com número central — os donuts da TELA-01 (Recebidos, Aguardando, Em
 * atendimento).
 *
 * Três decisões que a tela precisa herdar:
 *
 *  1. **A legenda traz o valor.** No tema claro, três cores da paleta ficam
 *     abaixo de 3:1 contra a superfície; o rótulo visível é a compensação
 *     exigida, e nenhuma informação depende só da cor.
 *  2. **Fatia com `onSelecionar` é clicável** e leva ao drill-down que o próprio
 *     servidor devolveu (`dados.*.drilldown`, `05-dashboard-metrics.md §4`). A
 *     tela não remonta o filtro: a conta tem uma definição só.
 *  3. **Frestas de 2px entre fatias**, na cor da superfície — sem elas, duas
 *     fatias vizinhas de matiz próximo viram uma só.
 */
export function Rosca({
  fatias,
  centro,
  legendaCentro,
  tamanho = 168,
  espessura = 18,
  maximoFatias = 6,
  className,
  onSelecionarCentro,
}: PropriedadesRosca) {
  const [emFoco, setEmFoco] = useState<number | null>(null)

  const preparadas = useMemo(() => {
    const ordenadas = [...fatias].sort((a, b) => b.valor - a.valor)
    if (ordenadas.length <= maximoFatias) return ordenadas

    const principais = ordenadas.slice(0, maximoFatias - 1)
    const resto = ordenadas.slice(maximoFatias - 1)
    return [
      ...principais,
      {
        rotulo: 'Outros',
        valor: resto.reduce((soma, fatia) => soma + fatia.valor, 0),
        cor: COR_RESTO,
      },
    ]
  }, [fatias, maximoFatias])

  const total = preparadas.reduce((soma, fatia) => soma + fatia.valor, 0)
  const raio = (tamanho - espessura) / 2
  const circunferencia = 2 * Math.PI * raio
  /** Fresta em unidades de arco. 2px de superfície entre fatias. */
  const fresta = total > 0 && preparadas.length > 1 ? 2 : 0

  let acumulado = 0

  return (
    <div className={cn('flex flex-col items-center gap-4', className)}>
      <div className="relative" style={{ width: tamanho, height: tamanho }}>
        <svg
          viewBox={`0 0 ${tamanho} ${tamanho}`}
          className="h-full w-full -rotate-90"
          role="img"
          aria-label={`Total ${typeof centro === 'number' ? centro : ''}${
            legendaCentro ? ` — ${legendaCentro}` : ''
          }`}
        >
          <circle
            cx={tamanho / 2}
            cy={tamanho / 2}
            r={raio}
            fill="none"
            stroke="rgb(var(--cor-superficie-3))"
            strokeWidth={espessura}
          />
          {total > 0 &&
            preparadas.map((fatia, indice) => {
              const proporcao = fatia.valor / total
              const comprimento = Math.max(0, proporcao * circunferencia - fresta)
              const deslocamento = -acumulado * circunferencia
              acumulado += proporcao
              if (fatia.valor <= 0) return null

              return (
                <circle
                  key={`${fatia.rotulo}-${indice}`}
                  cx={tamanho / 2}
                  cy={tamanho / 2}
                  r={raio}
                  fill="none"
                  stroke={fatia.cor ?? CORES_SERIE[indice % CORES_SERIE.length]}
                  strokeWidth={emFoco === indice ? espessura + 3 : espessura}
                  strokeDasharray={`${comprimento} ${circunferencia - comprimento}`}
                  strokeDashoffset={deslocamento}
                  strokeLinecap="butt"
                  className={cn(
                    'transition-[stroke-width]',
                    fatia.onSelecionar && 'cursor-pointer',
                  )}
                  onMouseEnter={() => setEmFoco(indice)}
                  onMouseLeave={() => setEmFoco(null)}
                  onClick={fatia.onSelecionar}
                />
              )
            })}
        </svg>

        {/*
          CORRIGE O CI-3 de `docs/release/13-ci.md §7` (WCAG 2.1.1, RNF-113): o
          drill-down do centro era um `div onClick` e só respondia a mouse.

          `<button>` de verdade e **só quando há ação** — um botão sem ação seria um
          ponto de tabulação que não faz nada, o que é uma regressão de teclado, não
          uma correção. Sem `onSelecionarCentro`, o centro volta a ser o `div`
          decorativo que sempre foi, sem foco e sem papel.

          `aria-label` explícito porque `centro` é só o número: "128" não diz o que
          o clique faz. O `<svg role="img">` acima continua com o rótulo do total —
          este descreve a AÇÃO, e são coisas diferentes.
        */}
        {onSelecionarCentro ? (
          <button
            type="button"
            onClick={onSelecionarCentro}
            aria-label={
              legendaCentro === undefined
                ? 'Ver detalhamento do total'
                : `Ver detalhamento de ${legendaCentro}`
            }
            className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center rounded-full text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-500/45"
          >
            <ConteudoCentro centro={centro} legendaCentro={legendaCentro} />
          </button>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <ConteudoCentro centro={centro} legendaCentro={legendaCentro} />
          </div>
        )}
      </div>

      {/* Legenda com valor — a compensação de contraste, não enfeite. */}
      <ul className="flex w-full flex-col gap-1.5">
        {preparadas.map((fatia, indice) => {
          const percentual =
            fatia.percentual ?? (total > 0 ? Math.round((fatia.valor / total) * 1000) / 10 : 0)
          const conteudo = (
            <>
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-sm ring-1 ring-black/10"
                style={{ background: fatia.cor ?? CORES_SERIE[indice % CORES_SERIE.length] }}
              />
              <span className="min-w-0 flex-1 truncate text-conteudo-suave">{fatia.rotulo}</span>
              <span className="shrink-0 font-medium tabular-nums text-conteudo">{fatia.valor}</span>
              <span className="w-11 shrink-0 text-right tabular-nums text-conteudo-fraco">
                {percentual.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
              </span>
            </>
          )

          return (
            <li key={`${fatia.rotulo}-${indice}`}>
              {fatia.onSelecionar ? (
                <button
                  type="button"
                  onClick={fatia.onSelecionar}
                  onMouseEnter={() => setEmFoco(indice)}
                  onMouseLeave={() => setEmFoco(null)}
                  // `text-left` explícito: `<button>` centraliza texto por padrão do
                  // navegador, e o rótulo da fatia ficaria centralizado na linha.
                  className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-xs hover:bg-superficie-2"
                >
                  {conteudo}
                </button>
              ) : (
                <div className="flex w-full items-center gap-2 px-1 py-0.5 text-xs">{conteudo}</div>
              )}
            </li>
          )
        })}
        {preparadas.length === 0 && (
          <li className="px-1 text-xs text-conteudo-fraco">Sem dados no período.</li>
        )}
      </ul>
    </div>
  )
}
